import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';
import { useMediaQuery } from 'react-responsive';

import { MEDIA_QUERY_SMALL_SCREEN } from 'client/constants/mediaQueries';

import {
  DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_PENDING_APPROVALS,
} from 'common/dataTableConstants';

import {
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SENT,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
} from 'common/householdConstants';

import {
  getHouseholdEarliestApplicationStatus,
  makeApplicationStatusList,
} from 'common/householdApplication/applicationHelpers';

import {
  dataTable,
  actionDataTableReset,
  actionDataTableLoadNextPage,
} from 'client/helpers/dataTables';

import withInfiniteScroll from 'client/helpers/withInfiniteScroll';
import branch from 'client/core/branch';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrPartnerList from 'client/components/SrPartnerList';
import SrPartnerSection from 'client/components/SrPartnerSection';
import SrUserSettingDropdownWithSearchBar from 'client/components/SrUserSettingDropdownWithSearchBar/SrUserSettingDropdownWithSearchBar';
import SrPartnerHouseholdPendingApprovalCard from 'client/components/SrPartnerHouseholdPendingApprovalCard';
import SrDataRetrievalText from 'client/components/SrDataRetrievalText/SrDataRetrievalText';

import { isListingTenantScreening } from 'common/listingHelpers';

const VIEW_ID_NEED_APPROVAL = 'VIEW_ID_NEED_APPROVAL';
const VIEW_ID_APPROVED = 'VIEW_ID_APPROVED';

const filterOptions = [
  {
    label: 'Need Approval',
    value: VIEW_ID_NEED_APPROVAL,
  },
  {
    label: 'Approved',
    value: VIEW_ID_APPROVED,
  },
];

function getDataTableSettingsFromProps(props) {
  const viewIdSuffix = lodashCompact([
    props.partner.id,
  ]).join('_');

  const marketIds = props.currentUser.settings.partnerPortal?.pendingApprovals?.[props.partner.slug + '_marketIds']; // eslint-disable-line max-len
  const viewId = `${DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_PENDING_APPROVALS}_${viewIdSuffix}`;
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_PENDING_APPROVALS,
    viewId,
    filter: {
      'listings.partnerId': props.partner.id,
    },
    count: 50,
    customParams: {},
  };

  if (marketIds && marketIds.length > 0) {
    dataTableSettings.customParams = {
      partnerApprovals: {
        marketIds: marketIds,
      },
    };
    dataTableSettings.rootViewId = `${viewId}_marketIds:${marketIds.join('_')}`; // eslint-disable-line max-len
    dataTableSettings.viewId = `${viewId}_marketIds:${marketIds.join('_')}`; // eslint-disable-line max-len
  }

  return dataTableSettings;
}

const LocalContainer = ({ children }) => {
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });

  return (
    <SrBox
      margin={isSmallScreen ? '0 19px 0 0' : '0 0 0 25px'}
    >
      {children}
    </SrBox>
  );
};

@dataTable(getDataTableSettingsFromProps)
@withInfiniteScroll({
  offset: 80,
})
@branch({
  currentUser: ['currentUser'],
  totalPendingApprovals: ['dataTables', DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_PENDING_APPROVALS],
})
class SrDataTablePartnerHouseholdPendingApprovals extends Component {
  static propTypes = {
    partner: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    appUrl: PropTypes.string.isRequired,
    currentUser: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);

    this.state = {
      viewId: VIEW_ID_NEED_APPROVAL,
      hasInitializedApps: false,
      households: [],
    };

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
  }

  UNSAFE_componentWillReceiveProps() {
    this._changeViewId(this.state.viewId);
  }

  componentWillUnmount() {
    this._isUnmounting = true;
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(
        actionDataTableLoadNextPage,
        getDataTableSettingsFromProps(this.props),
      );
    }
  }

  _handleInitializeApps = () => {
    const { viewId } = this.state;
    const filteredHouseholds = this._getHouseholds(viewId);
    this.setState((prevState) => ({
      ...prevState,
      hasInitializedApps: true,
      households: filteredHouseholds,
    }));
  }

  _changeViewId = (viewId) => {
    const filteredHouseholds = this._getHouseholds(viewId);
    this.setState((prevState) => ({
      ...prevState,
      viewId,
      households: filteredHouseholds,
    }));
  }

  _getApprovedApplicationsListingIds = () => {
    const approvedApps = this._getApprovedApplications();
    return approvedApps.map((app) => app.listingId);
  }

  _getNeedApprovalApplications = () => {
    const { items } = this.props;
    const alreadyApprovedApps = this._getApprovedApplicationsListingIds();
    return items.filter((item) => {
      return getHouseholdEarliestApplicationStatus(item.householdApplications) === HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD // eslint-disable-line max-len
        && !alreadyApprovedApps.includes(item.listingId);
    });
  }

  _getApprovedApplications = () => {
    const { items } = this.props;

    const validStatuses = makeApplicationStatusList({
      fromApplicationStatus: HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
      toApplicationStatus: HOUSEHOLD_APPLICATION_STATUS_LEASE_SENT,
    });

    return items.filter((item) => {
      const earliestAppStatus = getHouseholdEarliestApplicationStatus(item.householdApplications);
      return earliestAppStatus !== HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD // eslint-disable-line max-len
        && validStatuses.includes(earliestAppStatus);
    });
  }

  _getHouseholds = (viewId) => {
    const displayingApproved = viewId === VIEW_ID_APPROVED;

    const households = displayingApproved
      ? this._getApprovedApplications()
      : this._getNeedApprovalApplications();

    const sortByFieldId = displayingApproved ? 'acceptedDate' : 'sentToPartnerDate';
    households.sort((a, b) => new Date(b[sortByFieldId]).getTime() - new Date(a[sortByFieldId]).getTime());

    return households;
  }

  _onApproveApp = () => {
    this.props.dispatch(
      actionDataTableReset,
      getDataTableSettingsFromProps(this.props),
    );
  }

  _onDenyApp = () => {
    this.props.dispatch(
      actionDataTableReset,
      getDataTableSettingsFromProps(this.props),
    );
  }

  render() {
    const {
      onInfiniteScrollWrapperRef,
      appUrl,
      partner,
      history,
      currentUser,
      dataTableIsFullyLoaded,
      dataTableApiState,
      totalPendingApprovals,
    } = this.props;

    const {
      viewId,
      households,
      hasInitializedApps,
    } = this.state;

    if (!hasInitializedApps && dataTableIsFullyLoaded) {
      this._handleInitializeApps();
    }

    if (
      (
        !dataTableApiState.inProgress
        && dataTableIsFullyLoaded
        && !totalPendingApprovals.items.length
      )
    ) {
      return null;
    }

    return (
      <div className='SrDataTablePartnerHouseholdPendingApprovals' ref={onInfiniteScrollWrapperRef}>
        <SrPartnerSection
          title='Applications'
          headerRow={
            <div className='flex' style={{ lineHeight: '40px' }}>
              {partner && partner.markets?.length > 1 && (
                <SrUserSettingDropdownWithSearchBar
                  title='Market'
                  notFoundTitle='No markets found'
                  settingField={`partnerPortal.pendingApprovals.${partner.slug}_marketIds`}
                  dropdownOptions={partner.markets.map((option) => ({
                    label: option.displayNameFull,
                    value: option.marketId,
                  }))}
                />
              )}
              {filterOptions.map(({ label, value }) => {
                const isActive = value === viewId;
                return (
                  <LocalContainer key={value}>
                    <SrText
                      size='large'
                      weight='medium'
                      color='blueGreycliff'
                      cursor={isActive ? null : 'pointer'}
                      decoration={isActive ? 'underline-yellow' : null}
                      font='greycliff'
                      opacity={isActive ? null : 0.5}
                      onClick={isActive ? null : () => this._changeViewId(value)}
                      className='partnerDataTableFilterOption'
                    >
                      {label}
                    </SrText>
                  </LocalContainer>
                );
              })}
            </div>
          }
        >
          {households && (
            <SrPartnerList>
              {households.map((household, index) => {
                const isTenantScreening = isListingTenantScreening(household.listing);
                const appCardKey = `${household.id}_${index}`;
                return (
                  <SrPartnerHouseholdPendingApprovalCard
                    key={appCardKey}
                    alwaysDenyButton={isTenantScreening}
                    household={household}
                    moveInDate={household.moveInDate}
                    householdListing={household.householdListing}
                    listing={household.listing}
                    concession={household.concession}
                    tenantScreeningCriteria={household.tenantScreeningCriteria}
                    renters={household.renters}
                    householdApplications={household.householdApplications}
                    householdGuarantors={household.householdGuarantors}
                    guarantorApplications={household.guarantorApplications}
                    tenantAlertReports={household.tenantAlertReports}
                    breedRestrictions={household.breedRestrictions}
                    propertyManager={household.propertyManager}
                    partner={partner}
                    history={history}
                    appUrl={appUrl}
                    dataSource={DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_PENDING_APPROVALS}
                    currentUser={currentUser}
                    leaseStartDate={household.leaseStartDate}
                    pendingLandlordDate={household.pendingLandlordDate}
                    onApprove={this._onApproveApp}
                    onDeny={this._onDenyApp}
                    hasMoreActiveApps={household.activeApps > 0}
                  />
                );
              })}
            </SrPartnerList>
          )}
          {totalPendingApprovals.items.length > 0 && households.length === 0 && (
            <SrDataRetrievalText
              text={viewId === VIEW_ID_NEED_APPROVAL
                ? 'No applications to review right now in the selected market!'
                : 'Applications you approve will show here'
              }
            />
          )}
        </SrPartnerSection>
      </div>
    );
  }
}

SrDataTablePartnerHouseholdPendingApprovals.displayName = 'SrDataTablePartnerHouseholdPendingApprovals';

export default SrDataTablePartnerHouseholdPendingApprovals;
